var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import '@/styles/CombatMaps/CombatMapsPage.less';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useApiInstance } from '@/api';
import CombatMapCharacterListItem from '@/components/CombatMaps/CombatMapCharacterListItem';
import CharacterDetailsSidebar from '@/components/Common/CharacterDetailsSidebar';
import InitiativeCard from '@/components/InitiativeTracker/InitiativeCard';
import Loader from '@/components/UI/Loader';
import { useApiContext } from '@/context/ApiContext';
import { useAuth } from '@/context/AuthContext';
import { sortCards } from '@/utils/sort';
const CombatMapsPage = () => {
    const { user, loading: isAuthLoading } = useAuth();
    const { useMockApi } = useApiContext();
    const api = useApiInstance();
    const combatTrackerApi = api.combatTrackerApiInstance;
    const characterApi = api.characterApiInstance;
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [combatMaps, setCombatMaps] = useState([]);
    const [selectedMap, setSelectedMap] = useState(null);
    const [mapCharacters, setMapCharacters] = useState([]);
    const [userCharacters, setUserCharacters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [newMapName, setNewMapName] = useState('');
    const [editMapName, setEditMapName] = useState('');
    const [editNameLoading, setEditNameLoading] = useState(false);
    const [sidebarChar, setSidebarChar] = useState(null);
    const [isSidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarIsEdit, setSidebarIsEdit] = useState(false);
    useEffect(() => {
        if (isAuthLoading)
            return;
        if (user === null) {
            navigate('/auth');
            return;
        }
        if (useMockApi) {
            // Если используется mock API, перенаправляем на страницу трекера инициативы
            navigate('/initiative-tracker', { replace: true });
            return;
        }
        setLoading(true);
        Promise.all([
            combatTrackerApi.combat_map.list({ created_by_id: user === null || user === void 0 ? void 0 : user.id }),
            characterApi.list({ created_by_me: true }),
        ]).then(([maps, chars]) => {
            setCombatMaps(maps);
            setUserCharacters(sortCards(chars));
            setLoading(false);
        });
    }, [user, isAuthLoading, useMockApi, navigate, combatTrackerApi.combat_map, characterApi]);
    useEffect(() => {
        if (!selectedMap)
            return;
        setMapCharacters(sortCards(selectedMap.characters || []) || []);
        setEditMapName(selectedMap.name || '');
    }, [selectedMap, selectedMap === null || selectedMap === void 0 ? void 0 : selectedMap.characters]);
    const handleSelectMap = (map) => {
        setSelectedMap(map);
    };
    const refreshMaps = () => __awaiter(void 0, void 0, void 0, function* () {
        const maps = yield combatTrackerApi.combat_map.list({ created_by_id: user === null || user === void 0 ? void 0 : user.id });
        setCombatMaps(maps);
    });
    const updateMapCharactersInState = (mapId, updater) => {
        setCombatMaps((prevMaps) => prevMaps.map((map) => {
            if (map.id !== mapId)
                return map;
            const nextCharacters = updater(map.characters || []);
            return Object.assign(Object.assign({}, map), { characters: nextCharacters });
        }));
        setSelectedMap((prevSelectedMap) => {
            if (!prevSelectedMap || prevSelectedMap.id !== mapId)
                return prevSelectedMap;
            const nextCharacters = updater(prevSelectedMap.characters || []);
            return Object.assign(Object.assign({}, prevSelectedMap), { characters: nextCharacters });
        });
    };
    const handleAddCharacter = (char) => __awaiter(void 0, void 0, void 0, function* () {
        if (!selectedMap)
            return;
        const mapId = selectedMap.id;
        setMapCharacters((prev) => sortCards([...prev, char]));
        updateMapCharactersInState(mapId, (chars) => sortCards([...chars, char]));
        combatTrackerApi.combat_map.addCharacterToMap(mapId, char.id).catch((error) => {
            console.error('Error adding character to map:', error);
            setMapCharacters((prev) => prev.filter((c) => c.id !== char.id));
            updateMapCharactersInState(mapId, (chars) => chars.filter((c) => c.id !== char.id));
        });
    });
    const handleRemoveCharacter = (char) => __awaiter(void 0, void 0, void 0, function* () {
        if (!selectedMap)
            return;
        const mapId = selectedMap.id;
        setMapCharacters((prev) => prev.filter((c) => c.id !== char.id));
        updateMapCharactersInState(mapId, (chars) => chars.filter((c) => c.id !== char.id));
        combatTrackerApi.combat_map.removeCharacterFromMap(mapId, char.id).catch((error) => {
            console.error('Error removing character from map:', error);
            setMapCharacters((prev) => sortCards([...prev, char]));
            updateMapCharactersInState(mapId, (chars) => sortCards([...chars, char]));
        });
    });
    const handleCreateMap = (e) => __awaiter(void 0, void 0, void 0, function* () {
        e.preventDefault();
        if (!newMapName.trim())
            return;
        setActionLoading(true);
        yield combatTrackerApi.combat_map.create({ name: newMapName });
        setNewMapName('');
        yield refreshMaps();
        setActionLoading(false);
    });
    const handleEditMapName = (e) => __awaiter(void 0, void 0, void 0, function* () {
        e.preventDefault();
        if (!selectedMap || !editMapName.trim())
            return;
        setEditNameLoading(true);
        yield combatTrackerApi.combat_map.update(selectedMap.id, { name: editMapName });
        yield refreshMaps();
        setEditNameLoading(false);
    });
    const openSidebar = (char) => {
        setSidebarChar(char);
        setSidebarIsEdit(true); // всегда режим редактирования
        setSidebarOpen(true);
    };
    const closeSidebar = () => {
        setSidebarOpen(false);
        setSidebarChar(null);
    };
    const handleSave = (character) => __awaiter(void 0, void 0, void 0, function* () {
        const updated = yield characterApi.update(character);
        // обновить персонажа в userCharacters и mapCharacters
        setUserCharacters((chars) => chars.map((c) => (c.id === updated.id ? updated : c)));
        setMapCharacters((chars) => chars.map((c) => (c.id === updated.id ? updated : c)));
        setSidebarChar(null);
        setSidebarOpen(false);
        refreshMaps();
    });
    const handleDelete = (character) => __awaiter(void 0, void 0, void 0, function* () {
        yield characterApi.delete(character.id);
        setUserCharacters((chars) => chars.filter((c) => c.id !== character.id));
        setMapCharacters((chars) => chars.filter((c) => c.id !== character.id));
        setSidebarOpen(false);
        refreshMaps();
    });
    const previewElement = sidebarChar ? (_jsx(InitiativeCard, { card: sidebarChar, onSelect: () => { }, isSelected: true, showInitiative: false, className: "character-selected-stats-sidebar" })) : null;
    if (loading)
        return _jsx(Loader, {});
    return (_jsxs("div", { className: "combat-maps-page", children: [_jsxs("div", { className: "combat-maps-list", children: [_jsx("h3", { children: t('prepareCombat.combatMapsList') }), _jsxs("form", { className: "combat-map-add-form", onSubmit: handleCreateMap, children: [_jsx("input", { type: "text", placeholder: t('prepareCombat.addCombatMap'), value: newMapName, onChange: (e) => setNewMapName(e.target.value), disabled: actionLoading, style: { flex: 1 } }), _jsx("button", { type: "submit", disabled: actionLoading || !newMapName.trim(), children: "+" })] }), _jsx("ul", { children: combatMaps.map((map) => (_jsxs("li", { style: {
                                background: (selectedMap === null || selectedMap === void 0 ? void 0 : selectedMap.id) === map.id ? '#00000015' : 'transparent',
                                fontWeight: (selectedMap === null || selectedMap === void 0 ? void 0 : selectedMap.id) === map.id ? 'bold' : 'normal',
                            }, onClick: () => handleSelectMap(map), children: [_jsx("span", { children: map.name }), _jsx("button", { className: "combat-map-delete-btn", title: t('common.delete'), onClick: (e) => {
                                        e.stopPropagation();
                                        (() => __awaiter(void 0, void 0, void 0, function* () {
                                            yield combatTrackerApi.combat_map.delete(map.id);
                                            if ((selectedMap === null || selectedMap === void 0 ? void 0 : selectedMap.id) === map.id)
                                                setSelectedMap(null);
                                            yield refreshMaps();
                                        }))();
                                    }, style: { marginLeft: 8 }, disabled: actionLoading, children: "\u00D7" })] }, map.id))) })] }), _jsx("div", { className: "combat-map-edit-container", children: selectedMap ? (_jsxs("div", { className: "wrapper", children: [_jsx("div", { className: "combat-map-edit-form", children: _jsxs("form", { onSubmit: handleEditMapName, children: [_jsx("input", { type: "text", value: editMapName, onChange: (e) => setEditMapName(e.target.value), disabled: editNameLoading, style: { flex: 1 } }), _jsx("button", { type: "submit", disabled: editNameLoading || !editMapName.trim(), children: t('common.save') })] }) }), _jsxs("div", { className: "combat-map-edit-columns", children: [_jsxs("div", { className: "combat-map-characters", children: [_jsx("h4", { children: t('prepareCombat.mapCharactersList') }), _jsx("ul", { children: mapCharacters.map((char) => (_jsx(CombatMapCharacterListItem, { character: char, actionDisabled: actionLoading, onOpenCharacter: openSidebar, onActionClick: handleRemoveCharacter, actionLabel: "-" }, char.id))) })] }), _jsxs("div", { className: "user-characters", children: [_jsx("h4", { children: t('prepareCombat.userCharactersList') }), _jsx("ul", { children: userCharacters
                                                .filter((char) => !mapCharacters.some((c) => c.id === char.id))
                                                .map((char) => (_jsx(CombatMapCharacterListItem, { character: char, actionDisabled: actionLoading, onOpenCharacter: openSidebar, onActionClick: handleAddCharacter, actionLabel: "+" }, char.id))) })] })] }), isSidebarOpen && (_jsx(CharacterDetailsSidebar, { character: sidebarChar, isOpen: isSidebarOpen, onClose: closeSidebar, onSave: handleSave, onDelete: handleDelete, previewElement: previewElement }))] })) : (_jsx("div", { style: { color: '#888' }, children: t('prepareCombat.selectCombatMapToEdit') })) })] }));
};
export default CombatMapsPage;
